import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Tag,
  TagLabel,
  Text,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo } from 'react';
import { CSVLink } from 'react-csv';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { cdqDetailTableHeaders, getReportingDatesString } from './utils';
import { Typography } from 'packages/common-frontend/components/Typography';
import { colors } from '~/packages/common-frontend/colors';
import { Icon, TagIcon } from '~/packages/common-frontend/components/icons';

const resultBadgeStyles = {
  pass: {
    colorScheme: 'green',
    icon: 'circle-check',
  },
  fail: {
    colorScheme: 'red',
    icon: 'exclamation-circle',
  },
  warn: {
    colorScheme: 'orange',
    icon: 'circle-check',
  },
} as const;

export const AcknowledgedModeBadge = ({
  numAcknowledged,
  numUnacknowledged,
}: {
  numAcknowledged: number;
  numUnacknowledged: number;
}) => {
  const numberOfFailures = numAcknowledged + numUnacknowledged;
  let badgeState: 'pass' | 'fail' | 'warn' = 'warn';
  let messageText: string = 'No Issues Identified';

  if (numberOfFailures === 0) {
    // no issues identified
    messageText = 'No Issues Identified';
    badgeState = 'pass';
  } else {
    // some issues are not acknowledged
    messageText = `${numAcknowledged} Issues Acknowledged`;
    badgeState = 'pass';
  }

  return (
    <Tag
      variant="solid-muted"
      colorScheme={resultBadgeStyles[badgeState].colorScheme}
      size="sm"
    >
      <TagIcon icon={resultBadgeStyles[badgeState].icon} />
      <TagLabel>{messageText}</TagLabel>
    </Tag>
  );
};

export const UnacknowledgedModeBadge = ({
  numAcknowledged: _unused,
  numUnacknowledged,
}: {
  numAcknowledged: number;
  numUnacknowledged: number;
}) => {
  let badgeState: 'pass' | 'fail' | 'warn' = 'warn';
  let messageText: string = 'No Issues Identified';

  if (numUnacknowledged === 0) {
    // no issues identified
    messageText = 'No Issues Identified';
    badgeState = 'pass';
  } else {
    // some issues are not acknowledged
    messageText = `${numUnacknowledged} Issue${
      numUnacknowledged > 1 ? 's' : ''
    } Unacknowledged`;
    badgeState = 'fail';
  }

  return (
    <Tag
      variant="solid-muted"
      colorScheme={resultBadgeStyles[badgeState].colorScheme}
      size="sm"
    >
      <TagIcon icon={resultBadgeStyles[badgeState].icon} />
      <TagLabel>{messageText}</TagLabel>
    </Tag>
  );
};

export const CollateralDataQualityCheckResultBadge = ({
  numAcknowledged,
  numUnacknowledged,
  mode,
}: {
  numAcknowledged: number;
  numUnacknowledged: number;
  mode: 'unacknowledged' | 'acknowledged';
}) => {
  return mode === 'acknowledged' ? (
    <AcknowledgedModeBadge
      numAcknowledged={numAcknowledged}
      numUnacknowledged={numUnacknowledged}
    />
  ) : (
    <UnacknowledgedModeBadge
      numAcknowledged={numAcknowledged}
      numUnacknowledged={numUnacknowledged}
    />
  );
};

export const CollateralDataQualityCheckReportingDatesBadge = ({
  reportingDates,
}: {
  reportingDates: Util.FullDateStr[];
}) => {
  const reportingDatesString = useMemo(() => {
    if (reportingDates.length === 0) {
      return null;
    }

    const ascReportingDates = reportingDates.sort();
    const firstReportingDate = ascReportingDates[0];
    const lastReportingDate = ascReportingDates[ascReportingDates.length - 1];

    return getReportingDatesString({
      firstReportingDate,
      lastReportingDate,
    });
  }, [reportingDates]);

  if (!reportingDatesString) {
    return null;
  }

  return (
    <Flex w="fit-content" alignItems="center" gap="8px">
      <Icon icon="calendar" color={colors.gray[500]} />
      <Typography colorScheme="gray" variant="base-muted">
        {reportingDatesString}
      </Typography>
    </Flex>
  );
};

export const LastCheckedBadge = ({ date }: { date: Date }) => {
  const lastCheckedDateStr = useMemo(
    () => commonDate.epochToLocaleDateString(commonDate.dateToEpoch(date)),
    [date],
  );
  return (
    <Text
      color={colors.gray[500]}
      fontSize="14px"
    >{`Last Checked: ${lastCheckedDateStr}`}</Text>
  );
};

export const CollateralDataQualityCheckResultActionsMenu = ({
  onViewSQLQuery,
  onClickArchiveConfig,
  athenaQueryDataItems,
  cdqConfigName,
}: {
  onViewSQLQuery: () => void;
  onClickArchiveConfig: () => Promise<void>;
  athenaQueryDataItems: any[] | undefined;
  cdqConfigName: string;
}) => {
  const fileDownloadHeaders = useMemo(() => {
    return cdqDetailTableHeaders.map((header) => ({
      label: commonUtils.toTitleCase(header),
      key: header,
    }));
  }, []);

  const currentDateTime = commonDate.currentDateTime();
  const filename = `${_.snakeCase(cdqConfigName)}-${currentDateTime}.csv`;

  return (
    <Menu>
      <MenuButton as={Button} variant="ghost" px="4px" mb="2px">
        <Icon icon="ellipsis-vertical" />
      </MenuButton>
      <Portal>
        <MenuList zIndex={1000}>
          <MenuItem icon={<Icon icon="box" />} onClick={onClickArchiveConfig}>
            Archive
          </MenuItem>
          {!!athenaQueryDataItems?.length && (
            <CSVLink
              data={athenaQueryDataItems}
              headers={fileDownloadHeaders}
              filename={filename}
            >
              <MenuItem icon={<Icon icon="arrow-down-to-bracket" />}>
                <Text>Download CSV</Text>
              </MenuItem>
            </CSVLink>
          )}
          <MenuItem icon={<Icon icon="box" />} onClick={onViewSQLQuery}>
            View SQL Query
          </MenuItem>
        </MenuList>
      </Portal>
    </Menu>
  );
};

export const CollateralDataQualityCheckNoConfigsDisplay = () => {
  return (
    <Flex alignItems="center" mt="24px">
      <Typography>No collateral data quality checks are configured.</Typography>
    </Flex>
  );
};

export const CollateralDataQualityCheckAddMenu = ({
  onClickAddCdqCheck,
}: {
  onClickAddCdqCheck: () => void;
}) => {
  return (
    <Button
      variant="secondary-muted"
      colorScheme="v2-aurora"
      size="lg"
      onClick={onClickAddCdqCheck}
      leftIcon={<Icon icon="plus" />}
    >
      Add Data Quality Check
    </Button>
  );
};

export const ManualSqlQueryHelperText = () => {
  return (
    <Box
      borderColor={colors.gray[200]}
      borderWidth="1px"
      borderRadius="8px"
      p="24px"
      color={colors.gray[500]}
      mb="15px"
    >
      <Text mb="16px">
        To ensure the returning table includes the required columns, follow
        these steps:
      </Text>
      <Text mb="8px">
        <Text as="span" fontWeight={500} color="black">
          1. Specify the Fields:
        </Text>
        &nbsp;Ensure that the
        <Text as="span" color={colors.info} fontWeight={500}>
          &nbsp;SELECT&nbsp;
        </Text>
        statement includes the required fields.
      </Text>
      {cdqDetailTableHeaders.map((requiredHeader) => (
        <Text
          ml="32px"
          color={colors.gray[500]}
          key={`helper-${requiredHeader}`}
          mb="8px"
        >
          {`- ${requiredHeader}`}
        </Text>
      ))}
      <Text mb="16px" ml="16px">
        Use
        <Text as="span" color={colors.info} fontWeight={500}>
          &nbsp;AS&nbsp;
        </Text>
        to rename existing fields. Eg.
        <Text as="span" color={colors.info} fontWeight={500}>
          &nbsp;SELECT&nbsp;
        </Text>
        reportingdate
        <Text as="span" color={colors.info} fontWeight={500}>
          &nbsp;AS&nbsp;
        </Text>
        currentReportingDate
      </Text>

      <Text mb="16px">
        <Text as="span" fontWeight={500} color="black">
          2. Identify the Table(s):
        </Text>
        &nbsp;Determine
        <Text as="span" color={colors.info} fontWeight={500}>
          &nbsp;FROM&nbsp;
        </Text>
        which table(s) these columns will be retrieved.
      </Text>
      <Text>
        <Text as="span" fontWeight={500} color="black">
          3. Apply Necessary Conditions:
        </Text>
        &nbsp;Use appropriate
        <Text as="span" color={colors.info} fontWeight={500}>
          &nbsp;WHERE&nbsp;
        </Text>
        clauses to filter the data as needed.
      </Text>
    </Box>
  );
};
